import { LayoutGrid, List, LucideIcon } from 'lucide-react';

export interface ViewToggleOption<T extends string = string> {
  id: T;
  label: string;
  icon: LucideIcon;
}

export interface ViewToggleProps<T extends string = string> {
  value: T;
  onChange: (id: T) => void;
  /**
   * Defaults to the card/table pair, which is what 21 of the 23 call sites
   * offer. Pass options only for a genuinely different set.
   */
  options?: Array<ViewToggleOption<T>>;
  className?: string;
}

const DEFAULT_OPTIONS: Array<ViewToggleOption<any>> = [
  { id: 'card', label: 'Card view', icon: LayoutGrid },
  { id: 'table', label: 'Table view', icon: List },
];

/**
 * The card/table view switch, in one place.
 *
 * Twenty-three files hand-rolled this icon pair, and twenty-two gave the
 * buttons no accessible name at all — the defect that caused the
 * LegislationPage test debacle, where a test had to select "the first button
 * with class bg-background-tertiary" because there was nothing else to select
 * by, and clicked the wrong control for its entire life.
 *
 * Every button here carries aria-label, title, and aria-pressed, inside a
 * labelled group. The visual treatment is the dominant one from the sweep:
 * active gets the primary tint, inactive gets the inset fill with the
 * neutral-200 hover step (the same base/hover pairing the P3-01 token
 * migration established).
 */
const ViewToggle = <T extends string = string>({
  value,
  onChange,
  options = DEFAULT_OPTIONS as Array<ViewToggleOption<T>>,
  className = '',
}: ViewToggleProps<T>) => (
  <div role="group" aria-label="View mode" className={`flex items-center gap-2 ${className}`}>
    {options.map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        aria-label={label}
        title={label}
        aria-pressed={value === id}
        className={`p-2 rounded-lg transition-colors ${
          value === id
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
            : 'bg-background-tertiary text-text-secondary hover:bg-neutral-200 dark:bg-background-dark-tertiary dark:text-text-dark-secondary dark:hover:bg-background-dark-quaternary'
        }`}
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
      </button>
    ))}
  </div>
);

export default ViewToggle;
